import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Form, GetRef, Input, InputRef, Select, Table } from 'antd'
import type { TableColumnsType, TableProps } from 'antd'
import { useContext, useMemo } from 'react'
import { HolderOutlined } from '@ant-design/icons'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

type FormInstance<T> = GetRef<typeof Form<T>>

const EditableContext = React.createContext<FormInstance<any> | null>(null)

type TableRowSelection<T> = TableProps<T>['rowSelection']

interface EditableRowProps {
  index: number
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string
  index: number
}

export const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext)

  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm()

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    index
  } = useSortable({ id: props['data-row-key'] })
  const [form] = Form.useForm()

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 1000 } : {})
  }

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  )

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <RowContext.Provider value={contextValue}>
          <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </RowContext.Provider>
      </EditableContext.Provider>
    </Form>
  )
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  editType: 'input' | 'select'
  valueOptions?: { label: string; value: string }[]
  dataIndex: any
  record: any
  handleSave: (record: any) => void
}

interface Props {
  columns: (ColumnTypes[number] & {
    editable?: boolean
    dataIndex: string
    valueOptions?: { label: string; value: string }[]
    editType?: 'input' | 'select'
  })[]
  rows: any[] | []
  rowCount?: number
  mainHeight?: number
  paginationModel?: {
    page: number
    pageSize: number
  }
  paginationMode?: 'client' | 'server'
  onPaginationModelChange?: any
  onFilterModelChange?: any
  onSortModelChange?: any
  onRowSelectionModelChange?: any
  onCellClick?: any
  onCellEditStop?: any
  onCellEditStart?: any
  processRowUpdate?: any
  onProcessRowUpdateError?: any
  autoPageSize?: boolean
  initialState?: any
  rowSelectionModel?: Array<number | string> | number | string
  rowHeight?: number
  disableRowSelectionOnClick?: boolean
  disableRowSelection?: boolean
  checkboxSelection?: boolean
  sx?: object
  isPagination?: boolean
  loading?: boolean
  slots?: any
  pageSizeOptions?: (
    | number
    | {
        value: number
        label: string
      }
  )[]
  disableColumnMenu?: boolean
  disableVirtualization?: boolean
  sortingMode?: 'client' | 'server'
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  editType,
  valueOptions,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false)
  const inputRef = React.useRef<InputRef | any>(null)
  const form = React.useContext(EditableContext)!

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
    }
  }, [editing])

  const toggleEdit = () => {
    setEditing(!editing)

    form.setFieldsValue({ [dataIndex]: record[dataIndex] })
  }

  const save = async () => {
    try {
      const values = await form.validateFields()

      toggleEdit()

      //

      if (values && values.type) {
        handleSave({ ...record, ...values, variantsType: values.type })
      } else handleSave({ ...record, ...values })
    } catch (errInfo) {
      //
    }
  }

  let childNode = children

  if (editable) {
    if (editing) {
      if (editType === 'select')
        childNode = (
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `${title} is required.`
              }
            ]}
          >
            <Select
              style={{ width: 100 }}
              onChange={save}
              options={valueOptions}
              ref={inputRef}
              onBlur={(e) => {
                //

                toggleEdit()
              }}
            />
          </Form.Item>
        )
      else
        childNode = (
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `${title} is required.`
              }
            ]}
          >
            <Input ref={inputRef} onPressEnter={save} onBlur={save} />
          </Form.Item>
        )
    } else
      childNode = (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {children}
        </div>
      )
  }

  return <td {...restProps}>{childNode}</td>
}

type EditableTableProps = Parameters<typeof Table>[0]

export type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const AntdTables = React.forwardRef((props: Props, ref) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  const [dataSource, setDataSource] = React.useState<any[]>(props.rows)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState<React.Key[]>(
    []
  )
  const pageSize = React.useMemo(() => {
    return props.initialState?.pagination?.paginationModel?.pageSize ?? 15
  }, [props.initialState])

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)

    //

    props.onPaginationModelChange(
      {
        page: value - 1,
        pageSize: pageSize
      },
      1
    )
  }

  React.useEffect(() => {
    setDataSource(props.rows)
  }, [props.rows])

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.key === active?.id
        )
        const overIndex = prevState.findIndex(
          (record) => record.key === over?.id
        )

        return arrayMove(prevState, activeIndex, overIndex)
      })
    }
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    //

    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKey: React.Key[], selectedRows: object[]) => {
      console.log(
        `selectedRowKey: ${selectedRowKey}`,
        `selectedRowKeys: ${selectedRowKeys}`
      )

      setSelectedRowKeys([...new Set([...selectedRowKey])])

      props.onRowSelectionModelChange(selectedRowKey, selectedRows)
    },
    selectedRowKeys
  }

  const handleSave = (row: any) => {
    const newData = [...props.rows]
    const index = newData.findIndex((item) => row.key === item.key)
    const item = newData[index]

    newData.splice(index, 1, {
      ...item,
      ...row
    })

    //

    props.processRowUpdate && props.processRowUpdate(row, newData)
  }

  const components = {
    body: {
      row: Row,
      cell: EditableCell
    }
  }
  const columns = props.columns.map((col) => {
    if (!col.editable) {
      return col
    }

    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        editType: col.editType,
        valueOptions: col.valueOptions,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave
      })
    }
  })

  React.useImperativeHandle(ref, () => {
    return {}
  })

  return (
    <div style={{ width: '100%' }}>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowSelection={
              props.disableRowSelection
                ? undefined
                : {
                    ...rowSelection
                  }
            }
            columns={columns as any}
            dataSource={dataSource.map((item: any) => {
              item.key = item.id

              return item
            })}
            components={components}
            pagination={
              props.isPagination
                ? false
                : {
                    total: props.rowCount,
                    defaultPageSize: props.paginationModel?.pageSize,
                    showSizeChanger: false,
                    position: ['none', 'bottomCenter'],
                    showQuickJumper: true
                  }
            }
            size="middle"
            onChange={(pagination, filters, sorter) => {
              //

              props.onPaginationModelChange &&
                props.onPaginationModelChange({
                  page: (pagination.current as number) - 1,
                  pageSize: pagination.pageSize
                })

              props.onFilterModelChange && props.onFilterModelChange(filters)

              props.onSortModelChange && props.onSortModelChange(sorter)
            }}
            scroll={{
              y: props.mainHeight ? `${props.mainHeight - 180}px` : 500
            }}
          />
        </SortableContext>
      </DndContext>
    </div>
  )
})
export default AntdTables
